import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    VStack,
    HStack,
    Heading,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Select,
    Card,
    CardBody,
    CardHeader,
    Badge,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    MenuDivider,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Spinner,
    Alert,
    AlertIcon,
    useColorModeValue,
    Flex,
    Text,
    Code,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    useToast,
    Tooltip,
    Progress
} from '@chakra-ui/react';
import {
    MdSearch,
    MdRefresh,
    MdVisibility,
    MdFileDownload,
    MdMoreVert,
    MdError,
    MdCheckCircle,
    MdSchedule,
    MdDelete,
    MdFilterList,
    MdSort,
    MdDataUsage,
    MdAssessment,
    MdTrendingUp,
    MdWarning,
    MdClose
} from 'react-icons/md';
import apiService from '../services/apiService';
import { formatDateTime, getStatusColor, formatNumber, getRelativeTime } from '../utils/helpers';
import JSONFormatter from '../components/Common/JSONFormatter';
import * as XLSX from 'xlsx';
import usageTracker from '../services/usageTracker';

/**
 * Report Results Management Page Component
 */
function ReportResult({ userProfile, appConfig }) {
    // State management
    const [results, setResults] = useState([]);
    const [statistics, setStatistics] = useState([]);
    const [clients, setClients] = useState([]);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [clientFilter, setClientFilter] = useState('all');
    const [reportFilter, setReportFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('executed_at_utc');
    const [sortDirection, setSortDirection] = useState('DESC');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalResults, setTotalResults] = useState(0);
    const [currentResult, setCurrentResult] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    
    // Auto-refresh toggle state
    const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);
    const [refreshMode, setRefreshMode] = useState('auto'); // 'auto' or 'manual'
    
    // Flags to prevent multiple initial loads and toasts
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
    
    // Modal states
    const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    // Refs for cleanup
    const refreshInterval = useRef(null);
    
    // Theme colors
    const cardBg = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const textColor = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const codeBg = useColorModeValue('gray.50', 'gray.900');
    const statBg = useColorModeValue('white', 'gray.700');
    const inputBg = useColorModeValue('white', 'gray.700');
    const selectBg = useColorModeValue('white', 'gray.700');
    const dataSummaryBg = useColorModeValue('blue.50', 'blue.900');
    const metadataSummaryBg = useColorModeValue('purple.50', 'purple.900');
    const errorSummaryBg = useColorModeValue('red.50', 'red.900');
    const errorBorderColor = useColorModeValue('red.200', 'red.600');
    
    const toast = useToast();

    /**
     * Initialize component and start auto-refresh
     * Sets up data loading and periodic updates with proper sequencing
     */
    useEffect(() => {
        loadInitialData();
        setupAutoRefresh();
        
        return () => {
            cleanup();
        };
    }, []);

    /**
     * Apply filters and pagination when dependencies change
     * Skip if initial data hasn't loaded yet to prevent duplicate calls
     */
    useEffect(() => {
        if (hasLoadedInitialData) {
            loadResults();
        }
    }, [searchTerm, clientFilter, reportFilter, statusFilter, sortBy, sortDirection, currentPage, pageSize, hasLoadedInitialData]);

    /**
     * Setup auto-refresh for report results and statistics
     * Refreshes both data types every 30 seconds when auto-refresh is enabled
     */
    const setupAutoRefresh = () => {
        if (!isAutoRefreshEnabled) {
            return;
        }

        const interval = appConfig?.app?.features?.autoRefresh?.reportResults || 30000;
        
        refreshInterval.current = setInterval(() => {
            if (isAutoRefreshEnabled) {
                loadResults(true); // Silent refresh
            }
        }, interval);
    };

    /**
     * Toggle between auto and manual refresh modes
     */
    const handleRefreshModeToggle = () => {
        const newMode = refreshMode === 'auto' ? 'manual' : 'auto';
        const newAutoEnabled = newMode === 'auto';
        
        setRefreshMode(newMode);
        setIsAutoRefreshEnabled(newAutoEnabled);
        
        if (newAutoEnabled) {
            setupAutoRefresh();
            showSuccessToast('Auto-refresh enabled - updates every 30 seconds');
        } else {
            if (refreshInterval.current) {
                clearInterval(refreshInterval.current);
                refreshInterval.current = null;
            }
            showSuccessToast('Switched to manual refresh mode');
        }
    };

    /**
     * Switch to manual mode when filters are applied
     * Prevents auto-refresh from interfering with user's filtering
     */
    const switchToManualMode = (reason = 'filter applied') => {
        if (isAutoRefreshEnabled) {
            setRefreshMode('manual');
            setIsAutoRefreshEnabled(false);
            
            if (refreshInterval.current) {
                clearInterval(refreshInterval.current);
                refreshInterval.current = null;
            }
        }
    };

    /**
     * Cleanup intervals on unmount
     * Prevents memory leaks and unnecessary API calls
     */
    const cleanup = () => {
        if (refreshInterval.current) {
            clearInterval(refreshInterval.current);
            refreshInterval.current = null;
        }
    };

    /**
     * Load initial data for results, clients, reports, and statistics
     * Fetches all required data with proper error handling and sequencing
     * Statistics will be loaded by loadResults() to avoid duplication
     */
    const loadInitialData = async () => {
        try {
            setLoading(true);
            
            // Load supporting data in parallel (excluding statistics as they'll be loaded with results)
            const [clientsResponse, reportsResponse] = await Promise.all([
                apiService.getClientsList({ size: 1000 }),
                apiService.getReports({ size: 1000 })
            ]);

            // Set clients data
            if (clientsResponse.success && clientsResponse.data) {
                const clientsData = clientsResponse.data.content || clientsResponse.data;
                const clientsArray = Array.isArray(clientsData) ? clientsData : [];
                setClients(clientsArray);
            }

            // Set reports data
            if (reportsResponse.success && reportsResponse.data) {
                const reportsData = reportsResponse.data.content || reportsResponse.data;
                const reportsArray = Array.isArray(reportsData) ? reportsData : [];
                setReports(reportsArray);
            }

            // Load initial results (this will also load statistics)
            await loadResults(false, true); // Not silent, but is initial load
            
            // Mark initial data as loaded
            setHasLoadedInitialData(true);
            setIsInitialLoad(false);

        } catch (error) {
            showErrorToast('Failed to load initial data');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Load results data with filtering and pagination
     * Uses proper API endpoints and handles client-side filtering when needed
     * Also refreshes statistics on every call for real-time updates
     */
    const loadResults = async (silent = false, isInitial = false) => {
        try {
            if (!silent && !isInitial) {
                setRefreshing(true);
            }

            // Build API parameters matching backend PageRequest structure
            const params = {
                page: currentPage,
                size: pageSize,
                sortBy: sortBy,
                sortDirection: sortDirection,
                search: searchTerm.trim() || undefined
            };

            // Choose appropriate API endpoint based on filters
            let resultsPromise;
            let endpointUsed = 'general';

            if (clientFilter !== 'all' && reportFilter !== 'all') {
                // Multiple filters - use general endpoint and handle filtering
                resultsPromise = apiService.getReportResultList(params);
                endpointUsed = 'general-with-filtering';
            } else if (clientFilter !== 'all') {
                // Client-specific endpoint
                resultsPromise = apiService.getReportResultsByClient(clientFilter, params);
                endpointUsed = 'client-specific';
            } else if (reportFilter !== 'all') {
                // Report-specific endpoint
                resultsPromise = apiService.getReportResultsByReport(reportFilter, params);
                endpointUsed = 'report-specific';
            } else if (statusFilter !== 'all') {
                // Status-specific endpoint
                resultsPromise = apiService.getReportResultsByStatus(statusFilter, params);
                endpointUsed = 'status-specific';
            } else {
                // General endpoint for all results
                resultsPromise = apiService.getReportResultList(params);
                endpointUsed = 'general';
            }

            // Parallel API calls for results and statistics
            const apiCalls = [
                resultsPromise,
                apiService.getReportResultStatistics()
            ];

            // Execute both calls in parallel
            const [resultsResponse, statisticsResponse] = await Promise.all(apiCalls);
            
            // Process results data
            if (resultsResponse.success && resultsResponse.data) {
                let resultsData = resultsResponse.data.content || resultsResponse.data;
                const pagination = resultsResponse.data.pagination || {};
                
                // Apply additional client-side filtering if multiple filters are used
                if (endpointUsed === 'general-with-filtering') {
                    resultsData = applyClientSideFiltering(resultsData);
                }
                
                setResults(Array.isArray(resultsData) ? resultsData : []);
                setTotalResults(pagination.total || resultsData.length || 0);
                setLastUpdated(new Date());
            } else {
                throw new Error(resultsResponse.message || 'Failed to load results');
            }

            // Process statistics data
            if (statisticsResponse.success && statisticsResponse.data) {
                const statsData = Array.isArray(statisticsResponse.data) ? statisticsResponse.data : [];
                const mappedStats = statsData.map(stat => ({
                    name: stat.label,
                    value: stat.count,
                    color: stat.trend,
                    icon: stat.period,
                    percentage: stat.percentage
                }));
                setStatistics(mappedStats);
            }
            
            // Only show toast for manual refreshes, not initial load or silent refreshes
            if (!silent && !isInitial && !isInitialLoad) {
                showSuccessToast('Results and statistics updated successfully');
            }

        } catch (error) {
            if (!silent && !isInitial) {
                showErrorToast('Failed to load report results');
            }
        } finally {
            if (!silent && !isInitial) {
                setRefreshing(false);
            }
        }
    };

    /**
     * Load ALL results across all pages for export functionality
     * Fetches complete dataset with current filters applied
     */
    const loadAllResultsForExport = async () => {
        try {
            // Build parameters to get ALL data with current filters
            const params = {
                page: 1,
                size: Math.max(totalResults, 10000), // Use total count or large number
                sortBy: sortBy,
                sortDirection: sortDirection,
                search: searchTerm.trim() || undefined
            };

            // Use the same endpoint logic as regular loading
            let resultsPromise;

            if (clientFilter !== 'all' && reportFilter !== 'all') {
                resultsPromise = apiService.getReportResultList(params);
            } else if (clientFilter !== 'all') {
                resultsPromise = apiService.getReportResultsByClient(clientFilter, params);
            } else if (reportFilter !== 'all') {
                resultsPromise = apiService.getReportResultsByReport(reportFilter, params);
            } else if (statusFilter !== 'all') {
                resultsPromise = apiService.getReportResultsByStatus(statusFilter, params);
            } else {
                resultsPromise = apiService.getReportResultList(params);
            }

            const response = await resultsPromise;

            if (response.success && response.data) {
                let allResultsData = response.data.content || response.data;
                
                // Apply client-side filtering if needed
                if (clientFilter !== 'all' && reportFilter !== 'all') {
                    allResultsData = applyClientSideFiltering(allResultsData);
                }
                
                return Array.isArray(allResultsData) ? allResultsData : [];
            }
            
            throw new Error(response.message || 'Failed to load all results');
        } catch (error) {
            throw new Error(`Failed to fetch complete results data: ${error.message}`);
        }
    };

    /**
     * Apply client-side filtering when multiple filters are active
     * Used when backend endpoints don't support multiple filter combinations
     */
    const applyClientSideFiltering = (data) => {
        if (!Array.isArray(data)) return data;

        return data.filter(result => {
            // Apply client filter
            if (clientFilter !== 'all') {
                if (result.clientId.toString() !== clientFilter.toString()) {
                    return false;
                }
            }

            // Apply report filter
            if (reportFilter !== 'all') {
                if (result.reportCode.toString() !== reportFilter.toString()) {
                    return false;
                }
            }

            // Apply status filter
            if (statusFilter !== 'all') {
                const resultStatus = (result.executionStatus || '').toLowerCase();
                if (resultStatus !== statusFilter.toLowerCase()) {
                    return false;
                }
            }

            return true;
        });
    };

    /**
     * Handle manual refresh button click
     * Refreshes both results and statistics data
     */
    const handleRefresh = () => {
        loadResults();
    };

    /**
     * Handle search input change with debouncing and auto-switch to manual mode
     */
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to first page on search
        
        // Switch to manual mode when search is applied
        if (e.target.value.trim() !== '') {
            switchToManualMode('search applied');
        }
    };

    /**
     * Handle filter changes with auto-switch to manual mode
     */
    const handleFilterChange = (filterType, value) => {
        setCurrentPage(1); // Reset to first page on filter change
        
        // Switch to manual mode when any filter is applied
        switchToManualMode(`${filterType} filter applied`);
        
        switch (filterType) {
            case 'client':
                setClientFilter(value);
                break;
            case 'report':
                setReportFilter(value);
                break;
            case 'status':
                setStatusFilter(value);
                break;
            default:
                break;
        }
    };

    /**
     * Handle sort changes
     */
    const handleSortChange = (field) => {
        if (sortBy === field) {
            setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(field);
            setSortDirection('DESC');
        }
        setCurrentPage(1); // Reset to first page on sort change
    };

    /**
     * Handle pagination changes
     */
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    /**
     * Check if any filters are active
     */
    const hasActiveFilters = () => {
        return searchTerm !== '' || 
               clientFilter !== 'all' || 
               reportFilter !== 'all' || 
               statusFilter !== 'all';
    };

    /**
     * Get count of active filters
     */
    const getActiveFilterCount = () => {
        let count = 0;
        if (searchTerm !== '') count++;
        if (clientFilter !== 'all') count++;
        if (reportFilter !== 'all') count++;
        if (statusFilter !== 'all') count++;
        return count;
    };

    /**
     * Get display name for sort field
     */
    const getSortDisplayName = (field) => {
        const sortNames = {
            'client_id': 'Client ID',
            'report_code': 'Report Code',
            'execution_status': 'Status',
            'executed_at_utc': 'Execution Time',
            'updated_at_utc': 'Updated Time',
            'client_name': 'Client Name',
            'report_description': 'Report Description'
        };
        return sortNames[field] || field;
    };

    /**
     * Reset all filters to default values and optionally reset refresh mode
     */
    const handleResetFilters = () => {
        setSearchTerm('');
        setClientFilter('all');
        setReportFilter('all');
        setStatusFilter('all');
        setCurrentPage(1);
        
        // Option to reset to auto-refresh mode when filters are cleared
        if (refreshMode === 'manual' && !hasActiveFilters()) {
            setRefreshMode('auto');
            setIsAutoRefreshEnabled(true);
            setupAutoRefresh();
            showSuccessToast('Filters reset and auto-refresh enabled');
        } else {
            showSuccessToast('Filters reset successfully');
        }
    };

    /**
     * Format result data for optimal display in JSONFormatter
     * Handles different data types and ensures proper JSON formatting
     */
    const formatResultDataForDisplay = (resultData) => {
        try {
            if (!resultData) {
                return '';
            }

            // If it's already a string, try to parse and reformat
            if (typeof resultData === 'string') {
                try {
                    // Try to parse as JSON first
                    const parsed = JSON.parse(resultData);
                    return JSON.stringify(parsed, null, 2);
                } catch (parseError) {
                    // If not valid JSON, return as is but with some basic formatting
                    return resultData;
                }
            }

            // If it's an object, stringify with proper formatting
            if (typeof resultData === 'object') {
                return JSON.stringify(resultData, null, 2);
            }

            // For other types, convert to string
            return String(resultData);

        } catch (error) {
            return 'Error formatting data: ' + String(resultData);
        }
    };

    /**
     * Format data size for display
     * Shows human-readable size information
     */
    const formatDataSize = (data) => {
        try {
            const dataString = typeof data === 'string' ? data : JSON.stringify(data);
            const bytes = new Blob([dataString]).size;
            
            if (bytes === 0) return '0 Bytes';
            
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        } catch (error) {
            return 'Unknown size';
        }
    };

    /**
     * Open result detail modal
     * Shows comprehensive result information with JSON formatting
     */
    const openViewModal = (result) => {
        setCurrentResult(result);
        onViewOpen();
    };

    /**
     * Open delete confirmation modal
     */
    const openDeleteModal = (result) => {
        setCurrentResult(result);
        onDeleteOpen();
    };

    /**
     * Handle result deletion
     */
    const handleDeleteResult = async () => {
        if (!currentResult) return;

        try {
            const response = await apiService.deleteReportResult(
                currentResult.clientId,
                currentResult.reportCode
            );

            if (response.success) {
                showSuccessToast('Result deleted successfully');
                onDeleteClose();
                await loadResults();
            } else {
                throw new Error(response.message || 'Delete failed');
            }
        } catch (error) {
            showErrorToast('Failed to delete result');
        }
    };

    /**
     * Export ALL results to Excel with multiple sheets
     * Downloads complete filtered results as comprehensive Excel workbook
     * Now properly fetches ALL pages of data, not just current page
     */
    const handleExportResults = async () => {
        try {
            setExportLoading(true);

            // Step 1: Fetch ALL results across all pages with current filters
            const allResultsData = await loadAllResultsForExport();

            if (!allResultsData || allResultsData.length === 0) {
                showErrorToast('No data available for export');
                return;
            }

            // Create a new workbook
            const workbook = XLSX.utils.book_new();

            // Prepare main results data with detailed information
            const resultsData = allResultsData.map(result => ({
                'Client ID': result.clientId,
                'Client Name': result.clientName || '',
                'Report Code': result.reportCode,
                'Report Description': result.reportDescription || '',
                'Execution Status': result.statusDisplayName || result.executionStatus,
                'Executed At': formatDateTime(result.executedAtUtc),
                'Row Count': result.resultRowCount || 0,
                'Has Data': result.hasData ? 'Yes' : 'No',
                'Has Error': result.hasError ? 'Yes' : 'No',
                'Is Successful': result.isSuccessful ? 'Yes' : 'No',
                'Is Pending': result.isPending ? 'Yes' : 'No',
                'Time Ago': result.timeAgo || getRelativeTime(result.executedAtUtc),
                'Updated At': formatDateTime(result.updatedAtUtc || result.executedAtUtc),
                'Error Message': result.errorMessage || '',
                'Status CSS Class': result.statusCssClass || '',
                'Status Icon': result.statusIcon || '',
                'Result Data Preview': result.resultData ? 
                    (typeof result.resultData === 'string' ? 
                        result.resultData.substring(0, 500) + (result.resultData.length > 500 ? '...' : '') :
                        JSON.stringify(result.resultData).substring(0, 500) + '...'
                    ) : ''
            }));

            // Create main results worksheet
            const resultsWS = XLSX.utils.json_to_sheet(resultsData);
            
            // Set column widths for better readability
            const resultsColWidths = [
                { wch: 10 }, // Client ID
                { wch: 25 }, // Client Name
                { wch: 12 }, // Report Code
                { wch: 30 }, // Report Description
                { wch: 15 }, // Execution Status
                { wch: 20 }, // Executed At
                { wch: 10 }, // Row Count
                { wch: 10 }, // Has Data
                { wch: 10 }, // Has Error
                { wch: 12 }, // Is Successful
                { wch: 12 }, // Is Pending
                { wch: 15 }, // Time Ago
                { wch: 20 }, // Updated At
                { wch: 40 }, // Error Message
                { wch: 15 }, // Status CSS Class
                { wch: 12 }, // Status Icon
                { wch: 50 }  // Result Data Preview
            ];
            resultsWS['!cols'] = resultsColWidths;

            // Add results sheet to workbook
            XLSX.utils.book_append_sheet(workbook, resultsWS, 'Report Results');

            // Create detailed result data sheet for results that have data
            const resultsWithData = allResultsData.filter(result => result.resultData && result.hasData);
            if (resultsWithData.length > 0) {
                const detailedData = resultsWithData.map(result => ({
                    'Client ID': result.clientId,
                    'Report Code': result.reportCode,
                    'Client Name': result.clientName || '',
                    'Report Description': result.reportDescription || '',
                    'Executed At': formatDateTime(result.executedAtUtc),
                    'Full Result Data': typeof result.resultData === 'string' ? 
                        result.resultData : 
                        JSON.stringify(result.resultData, null, 2),
                    'Metadata': result.metadata ? JSON.stringify(result.metadata, null, 2) : ''
                }));

                const detailedWS = XLSX.utils.json_to_sheet(detailedData);
                
                // Set column widths for detailed data
                const detailedColWidths = [
                    { wch: 10 }, // Client ID
                    { wch: 12 }, // Report Code
                    { wch: 25 }, // Client Name
                    { wch: 30 }, // Report Description
                    { wch: 20 }, // Executed At
                    { wch: 80 }, // Full Result Data
                    { wch: 40 }  // Metadata
                ];
                detailedWS['!cols'] = detailedColWidths;

                XLSX.utils.book_append_sheet(workbook, detailedWS, 'Detailed Result Data');
            }

            // Create error details sheet for results that have errors
            const resultsWithErrors = allResultsData.filter(result => result.hasError && result.errorMessage);
            if (resultsWithErrors.length > 0) {
                const errorData = resultsWithErrors.map(result => ({
                    'Client ID': result.clientId,
                    'Report Code': result.reportCode,
                    'Client Name': result.clientName || '',
                    'Report Description': result.reportDescription || '',
                    'Executed At': formatDateTime(result.executedAtUtc),
                    'Execution Status': result.statusDisplayName || result.executionStatus,
                    'Error Message': result.errorMessage,
                    'Status CSS Class': result.statusCssClass || ''
                }));

                const errorWS = XLSX.utils.json_to_sheet(errorData);
                
                // Set column widths for error data
                const errorColWidths = [
                    { wch: 10 }, // Client ID
                    { wch: 12 }, // Report Code
                    { wch: 25 }, // Client Name
                    { wch: 30 }, // Report Description
                    { wch: 20 }, // Executed At
                    { wch: 15 }, // Execution Status
                    { wch: 60 }, // Error Message
                    { wch: 15 }  // Status CSS Class
                ];
                errorWS['!cols'] = errorColWidths;

                XLSX.utils.book_append_sheet(workbook, errorWS, 'Error Details');
            }

            // Create statistics summary sheet if statistics are available
            if (statistics.length > 0) {
                const statsData = statistics.map(stat => ({
                    'Metric': stat.name,
                    'Value': stat.value,
                    'Percentage': stat.percentage ? `${stat.percentage}%` : '',
                    'Color': stat.color || '',
                    'Icon': stat.icon || ''
                }));

                const statsWS = XLSX.utils.json_to_sheet(statsData);
                
                // Set column widths for statistics
                const statsColWidths = [
                    { wch: 20 }, // Metric
                    { wch: 15 }, // Value
                    { wch: 12 }, // Percentage
                    { wch: 15 }, // Color
                    { wch: 15 }  // Icon
                ];
                statsWS['!cols'] = statsColWidths;

                XLSX.utils.book_append_sheet(workbook, statsWS, 'Statistics');
            }

            // Create filter summary sheet
            const filterSummary = [
                { 'Filter Type': 'Search Term', 'Applied Value': searchTerm || 'None' },
                { 'Filter Type': 'Client Filter', 'Applied Value': clientFilter === 'all' ? 'All Clients' : clients.find(c => c.clientId.toString() === clientFilter)?.clientName || clientFilter },
                { 'Filter Type': 'Report Filter', 'Applied Value': reportFilter === 'all' ? 'All Reports' : reports.find(r => r.reportCode.toString() === reportFilter)?.reportDescription || reportFilter },
                { 'Filter Type': 'Status Filter', 'Applied Value': statusFilter === 'all' ? 'All Status' : statusFilter },
                { 'Filter Type': 'Sort By', 'Applied Value': `${getSortDisplayName(sortBy)} (${sortDirection})` },
                { 'Filter Type': 'Page Size', 'Applied Value': pageSize },
                { 'Filter Type': 'Current Page', 'Applied Value': currentPage },
                { 'Filter Type': 'Total Results (Filtered)', 'Applied Value': allResultsData.length },
                { 'Filter Type': 'Total Results (Database)', 'Applied Value': totalResults },
                { 'Filter Type': 'Results with Data', 'Applied Value': resultsWithData.length },
                { 'Filter Type': 'Results with Errors', 'Applied Value': resultsWithErrors.length },
                { 'Filter Type': 'Statistics Count', 'Applied Value': statistics.length },
                { 'Filter Type': 'Data Last Updated', 'Applied Value': lastUpdated ? formatDateTime(lastUpdated) : 'Not available' },
                { 'Filter Type': 'Export Date', 'Applied Value': formatDateTime(new Date()) }
            ];

            const filterWS = XLSX.utils.json_to_sheet(filterSummary);
            
            // Set column widths for filter summary
            const filterColWidths = [
                { wch: 20 }, // Filter Type
                { wch: 40 }  // Applied Value
            ];
            filterWS['!cols'] = filterColWidths;

            XLSX.utils.book_append_sheet(workbook, filterWS, 'Export Details');

            // Generate filename with timestamp and filters
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
            let filename = `report_results_complete_${timestamp}`;
            
            // Add filter info to filename if any filters are active
            if (hasActiveFilters()) {
                const activeFilters = [];
                if (clientFilter !== 'all') activeFilters.push('client');
                if (reportFilter !== 'all') activeFilters.push('report');
                if (statusFilter !== 'all') activeFilters.push('status');
                if (searchTerm) activeFilters.push('search');
                
                if (activeFilters.length > 0) {
                    filename += `_filtered_${activeFilters.join('_')}`;
                }
            }
            
            filename += '.xlsx';

            // Write and download the Excel file
            XLSX.writeFile(workbook, filename);
            usageTracker.action('Report Results', 'EXPORT_EXCEL');

            // Show success toast
            showSuccessToast(`Complete export successful: ${allResultsData.length} records exported to ${filename}`);

        } catch (error) {
            showErrorToast('Failed to export Excel file. Please try again.');
        } finally {
            setExportLoading(false);
        }
    };

    /**
     * Export single result details to Excel
     * Downloads detailed information for a specific result
     */
    const handleExportSingleResult = (result) => {
        try {
            // Create a new workbook for single result
            const workbook = XLSX.utils.book_new();

            // Basic result information
            const basicInfo = [
                { 'Field': 'Client ID', 'Value': result.clientId },
                { 'Field': 'Client Name', 'Value': result.clientName || '' },
                { 'Field': 'Report Code', 'Value': result.reportCode },
                { 'Field': 'Report Description', 'Value': result.reportDescription || '' },
                { 'Field': 'Execution Status', 'Value': result.statusDisplayName || result.executionStatus },
                { 'Field': 'Executed At', 'Value': formatDateTime(result.executedAtUtc) },
                { 'Field': 'Updated At', 'Value': formatDateTime(result.updatedAtUtc || result.executedAtUtc) },
                { 'Field': 'Row Count', 'Value': result.resultRowCount || 0 },
                { 'Field': 'Has Data', 'Value': result.hasData ? 'Yes' : 'No' },
                { 'Field': 'Has Error', 'Value': result.hasError ? 'Yes' : 'No' },
                { 'Field': 'Is Successful', 'Value': result.isSuccessful ? 'Yes' : 'No' },
                { 'Field': 'Is Pending', 'Value': result.isPending ? 'Yes' : 'No' },
                { 'Field': 'Time Ago', 'Value': result.timeAgo || getRelativeTime(result.executedAtUtc) },
                { 'Field': 'Status CSS Class', 'Value': result.statusCssClass || '' },
                { 'Field': 'Status Icon', 'Value': result.statusIcon || '' }
            ];

            const basicWS = XLSX.utils.json_to_sheet(basicInfo);
            basicWS['!cols'] = [{ wch: 25 }, { wch: 50 }];
            XLSX.utils.book_append_sheet(workbook, basicWS, 'Basic Information');

            // Result data sheet
            if (result.resultData) {
                const resultDataText = typeof result.resultData === 'string' ? 
                    result.resultData : 
                    JSON.stringify(result.resultData, null, 2);
                
                const resultDataInfo = [
                    { 'Content Type': 'Result Data', 'Content': resultDataText }
                ];

                const resultDataWS = XLSX.utils.json_to_sheet(resultDataInfo);
                resultDataWS['!cols'] = [{ wch: 20 }, { wch: 80 }];
                XLSX.utils.book_append_sheet(workbook, resultDataWS, 'Result Data');
            }

            // Error information sheet
            if (result.hasError && result.errorMessage) {
                const errorInfo = [
                    { 'Error Type': 'Execution Error', 'Error Details': result.errorMessage }
                ];

                const errorWS = XLSX.utils.json_to_sheet(errorInfo);
                errorWS['!cols'] = [{ wch: 20 }, { wch: 60 }];
                XLSX.utils.book_append_sheet(workbook, errorWS, 'Error Details');
            }

            // Metadata sheet
            if (result.metadata && Object.keys(result.metadata).length > 0) {
                const metadataInfo = [
                    { 'Metadata Type': 'Execution Metadata', 'Metadata Content': JSON.stringify(result.metadata, null, 2) }
                ];

                const metadataWS = XLSX.utils.json_to_sheet(metadataInfo);
                metadataWS['!cols'] = [{ wch: 20 }, { wch: 60 }];
                XLSX.utils.book_append_sheet(workbook, metadataWS, 'Metadata');
            }

            // Generate filename for single result
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
            const filename = `result_details_client${result.clientId}_report${result.reportCode}_${timestamp}.xlsx`;

            // Write and download the Excel file
            XLSX.writeFile(workbook, filename);
            usageTracker.action('Report Results', 'EXPORT_EXCEL');

            // Show success toast
            showSuccessToast(`Result details exported: ${filename}`);

        } catch (error) {
            showErrorToast('Failed to export result details. Please try again.');
        }
    };

    /**
     * Get execution status icon based on backend statusIcon field
     */
    const getStatusIcon = (result) => {
        if (result.statusIcon) {
            switch (result.statusIcon) {
                case 'check-circle':
                    return MdCheckCircle;
                case 'x-circle':
                case 'error':
                    return MdError;
                case 'clock':
                case 'schedule':
                    return MdSchedule;
                default:
                    return MdSchedule;
            }
        }
        
        // Fallback based on status
        switch (result.executionStatus?.toLowerCase()) {
            case 'success':
                return MdCheckCircle;
            case 'error':
            case 'failed':
                return MdError;
            case 'pending':
            case 'running':
                return MdSchedule;
            default:
                return MdSchedule;
        }
    };

    /**
     * Get status color from backend statusCssClass or fallback
     */
    const getResultStatusColor = (result) => {
        if (result.statusCssClass) {
            if (result.statusCssClass.includes('success')) return 'green';
            if (result.statusCssClass.includes('error')) return 'red';
            if (result.statusCssClass.includes('warning')) return 'orange';
            if (result.statusCssClass.includes('pending')) return 'yellow';
        }
        
        // Fallback to helper function
        return getStatusColor(result.executionStatus);
    };

    /**
     * Show success toast notification
     */
    const showSuccessToast = (message) => {
        toast({
            title: message,
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top-right'
        });
    };

    /**
     * Show error toast notification
     */
    const showErrorToast = (message) => {
        toast({
            title: message,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right'
        });
    };

    /**
     * Calculate total pages for pagination
     */
    const totalPages = Math.ceil(totalResults / pageSize);

    /**
     * Generate page numbers for pagination display
     */
    const getPageNumbers = () => {
        const maxVisiblePages = 10; // Show more pages for better navigation
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
        
        // Adjust startPage if we're near the end
        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }
        
        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        
        return pages;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" h="50vh">
                <VStack spacing={4}>
                    <Spinner size="xl" color="brand.500" thickness="4px" />
                    <Text color={textColor}>Loading report results...</Text>
                </VStack>
            </Box>
        );
    }

    return (
        <Box p={6}>
            <VStack spacing={6} align="stretch">
                {/* Page Header with Refresh Mode Toggle */}
                <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                    <Box>
                        <Heading size="lg" color={headingColor} mb={2}>
                            Report Results
                        </Heading>
                        <Text color={textColor}>
                            View and analyze report execution results from all clients
                        </Text>
                        {lastUpdated && (
                            <HStack spacing={2} mt={1}>
                                <Text fontSize="sm" color={textColor}>
                                    Last updated: {formatDateTime(lastUpdated)}
                                </Text>
                                <Badge 
                                    colorScheme={refreshMode === 'auto' ? 'green' : 'orange'} 
                                    variant="subtle"
                                    fontSize="xs"
                                >
                                    {refreshMode === 'auto' ? 'Auto-refresh: 30s' : 'Manual refresh'}
                                </Badge>
                            </HStack>
                        )}
                    </Box>
                    <VStack spacing={3} align="end">
                        {/* Refresh Mode Toggle */}
                        <HStack spacing={3}>
                            <Text fontSize="sm" color={textColor} fontWeight="medium">
                                Refresh Mode:
                            </Text>
                            <Button
                                size="sm"
                                variant={refreshMode === 'auto' ? 'solid' : 'outline'}
                                colorScheme={refreshMode === 'auto' ? 'green' : 'gray'}
                                onClick={handleRefreshModeToggle}
                                leftIcon={refreshMode === 'auto' ? <MdSchedule /> : <MdRefresh />}
                            >
                                {refreshMode === 'auto' ? 'Auto (30s)' : 'Manual'}
                            </Button>
                        </HStack>
                        
                        {/* Action Buttons */}
                        <HStack spacing={3} flexShrink={0}>
                            <Button
                                leftIcon={<MdRefresh />}
                                onClick={handleRefresh}
                                isLoading={refreshing}
                                variant="outline"
                                size="sm"
                                colorScheme="brand"
                                loadingText="Refreshing..."
                            >
                                {refreshMode === 'manual' ? 'Refresh Now' : 'Refresh Data'}
                            </Button>
                            <Tooltip label="Export ALL filtered results to Excel (.xlsx) - All pages included" hasArrow placement="bottom">
                                <Button
                                    leftIcon={<MdFileDownload />}
                                    onClick={handleExportResults}
                                    variant="outline"
                                    size="sm"
                                    colorScheme="green"
                                    isLoading={exportLoading}
                                    loadingText="Exporting..."
                                    isDisabled={totalResults === 0}
                                >
                                    Export All Pages
                                </Button>
                            </Tooltip>
                        </HStack>
                    </VStack>
                </Flex>

                {/* Statistics Cards with Real-time Updates */}
                {statistics.length > 0 && (
                    <Box>
                        <Flex justify="space-between" align="center" mb={4}>
                            <Text fontSize="md" fontWeight="medium" color={headingColor}>
                                Live Statistics
                            </Text>
                            <HStack spacing={2}>
                                {refreshing && (
                                    <HStack spacing={2}>
                                        <Spinner size="sm" color="brand.500" />
                                        <Text fontSize="xs" color={textColor}>Updating...</Text>
                                    </HStack>
                                )}
                                {lastUpdated && (
                                    <Text fontSize="xs" color={textColor}>
                                        Updated: {formatDateTime(lastUpdated, { showDate: false })}
                                    </Text>
                                )}
                            </HStack>
                        </Flex>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                            {statistics.map((stat, index) => (
                                <Card key={index} bg={statBg} borderColor={borderColor}>
                                    <CardBody>
                                        <Stat>
                                            <HStack justify="space-between">
                                                <Box>
                                                    <StatLabel color={textColor} fontSize="sm">
                                                        {stat.name}
                                                    </StatLabel>
                                                    <StatNumber color={headingColor} fontSize="2xl">
                                                        {formatNumber(stat.value)}
                                                    </StatNumber>
                                                    {stat.name === 'Success Rate' && (
                                                        <StatHelpText color={textColor}>
                                                            {stat.value}% success rate
                                                        </StatHelpText>
                                                    )}
                                                </Box>
                                                <Box color={stat.color || 'gray.400'}>
                                                    {stat.icon === 'database' && <MdDataUsage size="24px" />}
                                                    {stat.icon === 'check-circle' && <MdCheckCircle size="24px" />}
                                                    {stat.icon === 'x-circle' && <MdError size="24px" />}
                                                    {stat.icon === 'clock' && <MdSchedule size="24px" />}
                                                    {stat.icon === 'trending-up' && <MdTrendingUp size="24px" />}
                                                    {stat.icon === 'users' && <MdAssessment size="24px" />}
                                                    {stat.icon === 'file-text' && <MdAssessment size="24px" />}
                                                </Box>
                                            </HStack>
                                        </Stat>
                                    </CardBody>
                                </Card>
                            ))}
                        </SimpleGrid>
                    </Box>
                )}

                {/* Enhanced Filters & Search with Better UX and Reset Option */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                        <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                            <HStack spacing={2}>
                                <MdFilterList />
                                <Heading size="md">Filters & Search</Heading>
                                {(searchTerm || clientFilter !== 'all' || reportFilter !== 'all' || statusFilter !== 'all') && (
                                    <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                                        {getActiveFilterCount()} active
                                    </Badge>
                                )}
                            </HStack>
                            <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="gray"
                                leftIcon={<MdRefresh />}
                                onClick={handleResetFilters}
                                isDisabled={!hasActiveFilters()}
                            >
                                Reset Filters
                            </Button>
                        </Flex>
                    </CardHeader>
                    <CardBody>
                        <VStack spacing={6} align="stretch">
                            {/* Search Section */}
                            <Box>
                                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>
                                    Search
                                </Text>
                                <InputGroup size="md">
                                    <InputLeftElement>
                                        <MdSearch color={textColor} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search by client name, report description, or status..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        bg={inputBg}
                                        _focus={{
                                            borderColor: 'brand.500',
                                            boxShadow: '0 0 0 1px brand.500'
                                        }}
                                    />
                                    {searchTerm && (
                                        <InputRightElement>
                                            <IconButton
                                                size="sm"
                                                variant="ghost"
                                                icon={<MdClose />}
                                                onClick={() => setSearchTerm('')}
                                                aria-label="Clear search"
                                            />
                                        </InputRightElement>
                                    )}
                                </InputGroup>
                            </Box>

                            {/* Filter Section */}
                            <Box>
                                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={3}>
                                    Filters
                                </Text>
                                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} w="full">
                                    {/* Client Filter */}
                                    <Box>
                                        <Text fontSize="xs" color={textColor} mb={2} fontWeight="medium">
                                            Client
                                            {clientFilter !== 'all' && (
                                                <Badge ml={2} colorScheme="blue" variant="outline" fontSize="2xs">
                                                    Filtered
                                                </Badge>
                                            )}
                                        </Text>
                                        <Select
                                            value={clientFilter}
                                            onChange={(e) => handleFilterChange('client', e.target.value)}
                                            bg={selectBg}
                                            borderColor={clientFilter !== 'all' ? 'brand.300' : borderColor}
                                            _focus={{
                                                borderColor: 'brand.500',
                                                boxShadow: '0 0 0 1px brand.500'
                                            }}
                                        >
                                            <option value="all">All Clients ({clients.length})</option>
                                            {clients.map(client => (
                                                <option key={client.clientId} value={client.clientId}>
                                                    {client.clientName} (ID: {client.clientId})
                                                </option>
                                            ))}
                                        </Select>
                                    </Box>

                                    {/* Report Filter */}
                                    <Box>
                                        <Text fontSize="xs" color={textColor} mb={2} fontWeight="medium">
                                            Report
                                            {reportFilter !== 'all' && (
                                                <Badge ml={2} colorScheme="blue" variant="outline" fontSize="2xs">
                                                    Filtered
                                                </Badge>
                                            )}
                                        </Text>
                                        <Select
                                            value={reportFilter}
                                            onChange={(e) => handleFilterChange('report', e.target.value)}
                                            bg={selectBg}
                                            borderColor={reportFilter !== 'all' ? 'brand.300' : borderColor}
                                            _focus={{
                                                borderColor: 'brand.500',
                                                boxShadow: '0 0 0 1px brand.500'
                                            }}
                                        >
                                            <option value="all">All Reports ({reports.length})</option>
                                            {reports.map(report => (
                                                <option key={report.reportCode} value={report.reportCode}>
                                                    {report.reportDescription} (Code: {report.reportCode})
                                                </option>
                                            ))}
                                        </Select>
                                    </Box>

                                    {/* Status Filter */}
                                    <Box>
                                        <Text fontSize="xs" color={textColor} mb={2} fontWeight="medium">
                                            Execution Status
                                            {statusFilter !== 'all' && (
                                                <Badge ml={2} colorScheme="blue" variant="outline" fontSize="2xs">
                                                    Filtered
                                                </Badge>
                                            )}
                                        </Text>
                                        <Select
                                            value={statusFilter}
                                            onChange={(e) => handleFilterChange('status', e.target.value)}
                                            bg={selectBg}
                                            borderColor={statusFilter !== 'all' ? 'brand.300' : borderColor}
                                            _focus={{
                                                borderColor: 'brand.500',
                                                boxShadow: '0 0 0 1px brand.500'
                                            }}
                                        >
                                            <option value="all">All Status</option>
                                            <option value="success">✅ Success</option>
                                            <option value="error">❌ Error</option>
                                            <option value="failed">🚫 Failed</option>
                                            <option value="pending">⏳ Pending</option>
                                            <option value="running">🔄 Running</option>
                                            <option value="processing">⚡ Processing</option>
                                            <option value="timeout">⏰ Timeout</option>
                                            <option value="cancelled">🛑 Cancelled</option>
                                        </Select>
                                    </Box>
                                </SimpleGrid>
                            </Box>

                            {/* Display Options Section */}
                            <Box>
                                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={3}>
                                    Display Options
                                </Text>
                                <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
                                    {/* Page Size Control */}
                                    <HStack spacing={3}>
                                        <Text fontSize="sm" color={textColor} fontWeight="medium">
                                            Results per page:
                                        </Text>
                                        <Select
                                            value={pageSize}
                                            onChange={(e) => setPageSize(Number(e.target.value))}
                                            size="sm"
                                            maxW="130px"
                                            bg={selectBg}
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </Select>
                                    </HStack>

                                    {/* Sort Information */}
                                    <HStack spacing={3}>
                                        <Text fontSize="sm" color={textColor}>
                                            Sort by:
                                        </Text>
                                        <Badge variant="outline" colorScheme="gray" fontSize="xs">
                                            {getSortDisplayName(sortBy)} ({sortDirection})
                                        </Badge>
                                        {refreshing && <Spinner size="sm" color="brand.500" />}
                                    </HStack>
                                </Flex>
                            </Box>

                            {/* Active Filters Summary */}
                            {hasActiveFilters() && (
                                <Box>
                                    <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>
                                        Active Filters
                                    </Text>
                                    <Flex wrap="wrap" gap={2}>
                                        {searchTerm && (
                                            <Badge
                                                variant="solid"
                                                colorScheme="blue"
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                pr={1}
                                            >
                                                Search: "{searchTerm}"
                                                <IconButton
                                                    size="xs"
                                                    variant="ghost"
                                                    icon={<MdClose />}
                                                    onClick={() => setSearchTerm('')}
                                                    aria-label="Remove search filter"
                                                    color="white"
                                                    _hover={{ bg: 'whiteAlpha.200' }}
                                                />
                                            </Badge>
                                        )}
                                        {clientFilter !== 'all' && (
                                            <Badge
                                                variant="solid"
                                                colorScheme="green"
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                pr={1}
                                            >
                                                Client: {clients.find(c => c.clientId.toString() === clientFilter)?.clientName || clientFilter}
                                                <IconButton
                                                    size="xs"
                                                    variant="ghost"
                                                    icon={<MdClose />}
                                                    onClick={() => handleFilterChange('client', 'all')}
                                                    aria-label="Remove client filter"
                                                    color="white"
                                                    _hover={{ bg: 'whiteAlpha.200' }}
                                                />
                                            </Badge>
                                        )}
                                        {reportFilter !== 'all' && (
                                            <Badge
                                                variant="solid"
                                                colorScheme="purple"
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                pr={1}
                                            >
                                                Report: {reports.find(r => r.reportCode.toString() === reportFilter)?.reportDescription || reportFilter}
                                                <IconButton
                                                    size="xs"
                                                    variant="ghost"
                                                    icon={<MdClose />}
                                                    onClick={() => handleFilterChange('report', 'all')}
                                                    aria-label="Remove report filter"
                                                    color="white"
                                                    _hover={{ bg: 'whiteAlpha.200' }}
                                                />
                                            </Badge>
                                        )}
                                        {statusFilter !== 'all' && (
                                            <Badge
                                                variant="solid"
                                                colorScheme="orange"
                                                display="flex"
                                                alignItems="center"
                                                gap={1}
                                                pr={1}
                                            >
                                                Status: {statusFilter}
                                                <IconButton
                                                    size="xs"
                                                    variant="ghost"
                                                    icon={<MdClose />}
                                                    onClick={() => handleFilterChange('status', 'all')}
                                                    aria-label="Remove status filter"
                                                    color="white"
                                                    _hover={{ bg: 'whiteAlpha.200' }}
                                                />
                                            </Badge>
                                        )}
                                    </Flex>
                                </Box>
                            )}
                        </VStack>
                    </CardBody>
                </Card>

                {/* Results Table */}
                <Card bg={cardBg} borderColor={borderColor}>
                    <CardHeader>
                        <HStack justify="space-between">
                            <Heading size="md">
                                Execution Results ({formatNumber(totalResults)} total)
                            </Heading>
                            <HStack spacing={2}>
                                <Text fontSize="sm" color={textColor}>
                                    Page {currentPage} of {totalPages}
                                </Text>
                                {refreshing && <Spinner size="sm" color="brand.500" />}
                            </HStack>
                        </HStack>
                    </CardHeader>
                    <CardBody p={0}>
                        <TableContainer>
                            <Table variant="simple" size="sm">
                                <Thead>
                                    <Tr>
                                        <Th>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                leftIcon={<MdSort />}
                                                onClick={() => handleSortChange('client_id')}
                                            >
                                                Client
                                            </Button>
                                        </Th>
                                        <Th>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                leftIcon={<MdSort />}
                                                onClick={() => handleSortChange('report_code')}
                                            >
                                                Report
                                            </Button>
                                        </Th>
                                        <Th>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                leftIcon={<MdSort />}
                                                onClick={() => handleSortChange('execution_status')}
                                            >
                                                Status
                                            </Button>
                                        </Th>
                                        <Th>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                leftIcon={<MdSort />}
                                                onClick={() => handleSortChange('executed_at_utc')}
                                            >
                                                Executed At
                                            </Button>
                                        </Th>
                                        <Th>Rows</Th>
                                        <Th>Time Ago</Th>
                                        <Th w="100px">Actions</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {results.map((result, index) => {
                                        const StatusIcon = getStatusIcon(result);
                                        const statusColor = getResultStatusColor(result);
                                        
                                        return (
                                            <Tr key={`${result.clientId}_${result.reportCode}_${index}`}>
                                                <Td>
                                                    <VStack align="start" spacing={1}>
                                                        <Text fontWeight="medium" fontSize="sm">
                                                            {result.clientName || `Client ${result.clientId}`}
                                                        </Text>
                                                        <Badge variant="outline" colorScheme="gray" fontSize="xs">
                                                            ID: {result.clientId}
                                                        </Badge>
                                                    </VStack>
                                                </Td>
                                                <Td>
                                                    <VStack align="start" spacing={1}>
                                                        <Badge colorScheme="blue" variant="subtle">
                                                            {result.reportCode}
                                                        </Badge>
                                                        {result.reportDescription && (
                                                            <Text fontSize="xs" color={textColor} noOfLines={1}>
                                                                {result.reportDescription}
                                                            </Text>
                                                        )}
                                                    </VStack>
                                                </Td>
                                                <Td>
                                                    <HStack spacing={2}>
                                                        <StatusIcon 
                                                            color={statusColor} 
                                                            size="16px"
                                                        />
                                                        <Badge
                                                            colorScheme={statusColor}
                                                            variant="subtle"
                                                        >
                                                            {result.statusDisplayName || result.executionStatus}
                                                        </Badge>
                                                    </HStack>
                                                </Td>
                                                <Td fontSize="sm" color={textColor}>
                                                    {formatDateTime(result.executedAtUtc)}
                                                </Td>
                                                <Td fontSize="sm">
                                                    <HStack spacing={2}>
                                                        <Text>{formatNumber(result.resultRowCount || 0)}</Text>
                                                        {result.hasData && (
                                                            <Badge colorScheme="green" variant="outline" size="sm">
                                                                Data
                                                            </Badge>
                                                        )}
                                                    </HStack>
                                                </Td>
                                                <Td fontSize="sm" color={textColor}>
                                                    {result.timeAgo || getRelativeTime(result.executedAtUtc)}
                                                </Td>
                                                <Td>
                                                    <Menu>
                                                        <MenuButton
                                                            as={IconButton}
                                                            icon={<MdMoreVert />}
                                                            variant="ghost"
                                                            size="sm"
                                                        />
                                                        <MenuList>
                                                            <MenuItem
                                                                icon={<MdVisibility />}
                                                                onClick={() => openViewModal(result)}
                                                            >
                                                                View Details
                                                            </MenuItem>
                                                            <MenuDivider />
                                                            <MenuItem
                                                                icon={<MdDelete />}
                                                                onClick={() => openDeleteModal(result)}
                                                                color="red.500"
                                                                _hover={{ bg: 'red.50' }}
                                                            >
                                                                Delete
                                                            </MenuItem>
                                                        </MenuList>
                                                    </Menu>
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </Tbody>
                            </Table>
                        </TableContainer>

                        {/* Enhanced Pagination Controls */}
                        {totalPages > 1 && (
                            <Flex justify="space-between" align="center" p={4} borderTop="1px" borderTopColor={borderColor}>
                                <Text fontSize="sm" color={textColor}>
                                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalResults)} of {formatNumber(totalResults)} entries
                                </Text>
                                
                                <HStack spacing={2}>
                                    <Button
                                        size="sm"
                                        onClick={() => handlePageChange(1)}
                                        isDisabled={currentPage === 1}
                                        variant="outline"
                                    >
                                        First
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        isDisabled={currentPage === 1}
                                        variant="outline"
                                    >
                                        Previous
                                    </Button>
                                    
                                    {/* Page Numbers */}
                                    {getPageNumbers().map(page => (
                                        <Button
                                            key={page}
                                            size="sm"
                                            variant={currentPage === page ? "solid" : "outline"}
                                            colorScheme={currentPage === page ? "brand" : "gray"}
                                            onClick={() => handlePageChange(page)}
                                        >
                                            {page}
                                        </Button>
                                    ))}
                                    
                                    <Button
                                        size="sm"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        isDisabled={currentPage === totalPages}
                                        variant="outline"
                                    >
                                        Next
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => handlePageChange(totalPages)}
                                        isDisabled={currentPage === totalPages}
                                        variant="outline"
                                    >
                                        Last
                                    </Button>
                                </HStack>
                            </Flex>
                        )}
                    </CardBody>
                </Card>

                {/* View Result Details Modal */}
                <Modal isOpen={isViewOpen} onClose={onViewClose} size="6xl">
                    <ModalOverlay />
                    <ModalContent maxH="90vh">
                        <ModalHeader>
                            <HStack spacing={3}>
                                <Text>Result Details</Text>
                                {currentResult && (
                                    <Badge colorScheme="blue">
                                        {currentResult.clientName} - Report {currentResult.reportCode}
                                    </Badge>
                                )}
                            </HStack>
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody overflowY="auto">
                            {currentResult && (
                                <Tabs>
                                    <TabList>
                                        <Tab>Execution Info</Tab>
                                        <Tab>Result Data</Tab>
                                        <Tab>Error Details</Tab>
                                        <Tab>Metadata</Tab>
                                    </TabList>

                                    <TabPanels>
                                        {/* Execution Info Tab */}
                                        <TabPanel>
                                            <VStack spacing={6} align="stretch">
                                                <SimpleGrid columns={2} spacing={6}>
                                                    <Box>
                                                        <Text fontWeight="bold" mb={2}>Client Information</Text>
                                                        <VStack align="start" spacing={2}>
                                                            <HStack>
                                                                <Text fontSize="sm" color={textColor}>ID:</Text>
                                                                <Text fontWeight="medium">{currentResult.clientId}</Text>
                                                            </HStack>
                                                            <HStack>
                                                                <Text fontSize="sm" color={textColor}>Name:</Text>
                                                                <Text fontWeight="medium">{currentResult.clientName || 'N/A'}</Text>
                                                            </HStack>
                                                        </VStack>
                                                    </Box>
                                                    
                                                    <Box>
                                                        <Text fontWeight="bold" mb={2}>Report Information</Text>
                                                        <VStack align="start" spacing={2}>
                                                            <HStack>
                                                                <Text fontSize="sm" color={textColor}>Code:</Text>
                                                                <Badge colorScheme="blue">{currentResult.reportCode}</Badge>
                                                            </HStack>
                                                            <HStack>
                                                                <Text fontSize="sm" color={textColor}>Description:</Text>
                                                                <Text fontWeight="medium">{currentResult.reportDescription || 'N/A'}</Text>
                                                            </HStack>
                                                        </VStack>
                                                    </Box>
                                                </SimpleGrid>

                                                <SimpleGrid columns={2} spacing={6}>
                                                    <Box>
                                                        <Text fontWeight="bold" mb={2}>Execution Status</Text>
                                                        <HStack>
                                                            {React.createElement(getStatusIcon(currentResult), {
                                                                color: getResultStatusColor(currentResult),
                                                                size: "20px"
                                                            })}
                                                            <Badge
                                                                colorScheme={getResultStatusColor(currentResult)}
                                                                variant="subtle"
                                                                fontSize="md"
                                                            >
                                                                {currentResult.statusDisplayName || currentResult.executionStatus}
                                                            </Badge>
                                                        </HStack>
                                                    </Box>
                                                    
                                                    <Box>
                                                        <Text fontWeight="bold" mb={2}>Timing</Text>
                                                        <VStack align="start" spacing={2}>
                                                            <HStack>
                                                                <Text fontSize="sm" color={textColor}>Executed:</Text>
                                                                <Text fontWeight="medium">{formatDateTime(currentResult.executedAtUtc)}</Text>
                                                            </HStack>
                                                            <HStack>
                                                                <Text fontSize="sm" color={textColor}>Time Ago:</Text>
                                                                <Text fontWeight="medium">{currentResult.timeAgo || getRelativeTime(currentResult.executedAtUtc)}</Text>
                                                            </HStack>
                                                        </VStack>
                                                    </Box>
                                                </SimpleGrid>

                                                <Box>
                                                    <Text fontWeight="bold" mb={2}>Result Summary</Text>
                                                    <SimpleGrid columns={3} spacing={4}>
                                                        <Stat>
                                                            <StatLabel>Row Count</StatLabel>
                                                            <StatNumber>{formatNumber(currentResult.resultRowCount || 0)}</StatNumber>
                                                        </Stat>
                                                        <Stat>
                                                            <StatLabel>Has Data</StatLabel>
                                                            <StatNumber color={currentResult.hasData ? 'green.500' : 'gray.500'}>
                                                                {currentResult.hasData ? 'Yes' : 'No'}
                                                            </StatNumber>
                                                        </Stat>
                                                        <Stat>
                                                            <StatLabel>Has Error</StatLabel>
                                                            <StatNumber color={currentResult.hasError ? 'red.500' : 'gray.500'}>
                                                                {currentResult.hasError ? 'Yes' : 'No'}
                                                            </StatNumber>
                                                        </Stat>
                                                    </SimpleGrid>
                                                </Box>
                                            </VStack>
                                        </TabPanel>

                                        {/* Result Data Tab */}
                                        <TabPanel>
                                            <Box>
                                                <Text fontWeight="bold" mb={4}>Result Data</Text>
                                                {currentResult.resultData ? (
                                                    <Box>
                                                        {/* Data Summary */}
                                                        <Box mb={4} p={3} bg={dataSummaryBg} borderRadius="md">
                                                            <HStack spacing={4} wrap="wrap">
                                                                <Text fontSize="sm" color="blue.600" fontWeight="medium">
                                                                    <strong>Data Type:</strong> {typeof currentResult.resultData === 'string' ? 'String' : 'Object'}
                                                                </Text>
                                                                <Text fontSize="sm" color="blue.600" fontWeight="medium">
                                                                    <strong>Size:</strong> {formatDataSize(currentResult.resultData)}
                                                                </Text>
                                                                {currentResult.resultRowCount && (
                                                                    <Text fontSize="sm" color="blue.600" fontWeight="medium">
                                                                        <strong>Rows:</strong> {formatNumber(currentResult.resultRowCount)}
                                                                    </Text>
                                                                )}
                                                            </HStack>
                                                        </Box>

                                                        {/* Formatted JSON Display */}
                                                        <JSONFormatter
                                                            value={formatResultDataForDisplay(currentResult.resultData)}
                                                            onChange={() => {}} // Read-only mode
                                                            placeholder="No result data available"
                                                            rows={25}
                                                            isDisabled={true}
                                                            autoHighlight={true}
                                                            showFormatButton={false}
                                                            showHighlightButton={true}
                                                            autoSize={true}
                                                            minRows={15}
                                                            maxRows={40}
                                                            bg={codeBg}
                                                        />

                                                        {/* Raw Data Option */}
                                                        <Box mt={4}>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                leftIcon={<MdVisibility />}
                                                                onClick={() => {
                                                                    const rawData = typeof currentResult.resultData === 'string' ? 
                                                                        currentResult.resultData : 
                                                                        JSON.stringify(currentResult.resultData);
                                                                    
                                                                    navigator.clipboard.writeText(rawData).then(() => {
                                                                        showSuccessToast('Raw data copied to clipboard');
                                                                    }).catch(() => {
                                                                        showErrorToast('Failed to copy to clipboard');
                                                                    });
                                                                }}
                                                            >
                                                                Copy Raw Data
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Alert status="info">
                                                        <AlertIcon />
                                                        No result data available for this execution
                                                    </Alert>
                                                )}
                                            </Box>
                                        </TabPanel>

                                        {/* Error Details Tab */}
                                        <TabPanel>
                                            <Box>
                                                <Text fontWeight="bold" mb={4}>Error Information</Text>
                                                {currentResult.errorMessage ? (
                                                    <Box>
                                                        {/* Error Summary */}
                                                        <Box mb={4} p={3} bg={errorSummaryBg} borderRadius="md">
                                                            <HStack spacing={4} wrap="wrap">
                                                                <Text fontSize="sm" color="red.600" fontWeight="medium">
                                                                    <strong>Error Status:</strong> {currentResult.executionStatus}
                                                                </Text>
                                                                <Text fontSize="sm" color="red.600" fontWeight="medium">
                                                                    <strong>Message Length:</strong> {currentResult.errorMessage.length} characters
                                                                </Text>
                                                                <Text fontSize="sm" color="red.600" fontWeight="medium">
                                                                    <strong>Occurred At:</strong> {formatDateTime(currentResult.executedAtUtc)}
                                                                </Text>
                                                            </HStack>
                                                        </Box>

                                                        {/* Error Message Display */}
                                                        <Alert status="error" borderRadius="md" mb={4}>
                                                            <AlertIcon />
                                                            <Box w="full">
                                                                <Text fontWeight="bold" mb={2}>Error Message:</Text>
                                                                <Code
                                                                    display="block"
                                                                    whiteSpace="pre-wrap"
                                                                    p={4}
                                                                    bg={codeBg}
                                                                    borderRadius="md"
                                                                    fontSize="sm"
                                                                    w="full"
                                                                    maxH="300px"
                                                                    overflowY="auto"
                                                                    border="1px solid"
                                                                    borderColor={errorBorderColor}
                                                                >
                                                                    {currentResult.errorMessage}
                                                                </Code>
                                                            </Box>
                                                        </Alert>

                                                        {/* Error Actions */}
                                                        <HStack spacing={3}>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                leftIcon={<MdVisibility />}
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(currentResult.errorMessage).then(() => {
                                                                        showSuccessToast('Error message copied to clipboard');
                                                                    }).catch(() => {
                                                                        showErrorToast('Failed to copy to clipboard');
                                                                    });
                                                                }}
                                                            >
                                                                Copy Error Message
                                                            </Button>

                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                colorScheme="blue"
                                                                leftIcon={<MdFileDownload />}
                                                                onClick={() => {
                                                                    const errorData = {
                                                                        clientId: currentResult.clientId,
                                                                        reportCode: currentResult.reportCode,
                                                                        executionStatus: currentResult.executionStatus,
                                                                        executedAt: currentResult.executedAtUtc,
                                                                        errorMessage: currentResult.errorMessage,
                                                                        statusCssClass: currentResult.statusCssClass
                                                                    };
                                                                    
                                                                    const blob = new Blob([JSON.stringify(errorData, null, 2)], 
                                                                        { type: 'application/json' });
                                                                    const url = URL.createObjectURL(blob);
                                                                    const a = document.createElement('a');
                                                                    a.href = url;
                                                                    a.download = `error_details_${currentResult.clientId}_${currentResult.reportCode}.json`;
                                                                    a.click();
                                                                    URL.revokeObjectURL(url);
                                                                }}
                                                            >
                                                                Download Error Report
                                                            </Button>
                                                        </HStack>
                                                    </Box>
                                                ) : (
                                                    <Alert status="success">
                                                        <AlertIcon />
                                                        <Box>
                                                            <Text fontWeight="bold">No Errors Reported</Text>
                                                            <Text fontSize="sm" mt={1}>
                                                                This execution completed without any reported errors.
                                                            </Text>
                                                        </Box>
                                                    </Alert>
                                                )}
                                            </Box>
                                        </TabPanel>

                                        {/* Metadata Tab */}
                                        <TabPanel>
                                            <VStack spacing={6} align="stretch">
                                                <Box>
                                                    <Text fontWeight="bold" mb={4}>Execution Metadata</Text>
                                                    {currentResult.metadata && Object.keys(currentResult.metadata).length > 0 ? (
                                                        <Box>
                                                            {/* Metadata Summary */}
                                                            <Box mb={4} p={3} bg={metadataSummaryBg} borderRadius="md">
                                                                <HStack spacing={4} wrap="wrap">
                                                                    <Text fontSize="sm" color="purple.600" fontWeight="medium">
                                                                        <strong>Properties:</strong> {Object.keys(currentResult.metadata).length}
                                                                    </Text>
                                                                    <Text fontSize="sm" color="purple.600" fontWeight="medium">
                                                                        <strong>Size:</strong> {formatDataSize(currentResult.metadata)}
                                                                    </Text>
                                                                </HStack>
                                                            </Box>

                                                            {/* Formatted Metadata Display */}
                                                            <JSONFormatter
                                                                value={JSON.stringify(currentResult.metadata, null, 2)}
                                                                onChange={() => {}} // Read-only mode
                                                                placeholder="No metadata available"
                                                                rows={15}
                                                                isDisabled={true}
                                                                autoHighlight={true}
                                                                showFormatButton={false}
                                                                showHighlightButton={true}
                                                                autoSize={true}
                                                                minRows={8}
                                                                maxRows={25}
                                                                bg={codeBg}
                                                            />

                                                            {/* Copy Metadata Button */}
                                                            <Box mt={4}>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    leftIcon={<MdVisibility />}
                                                                    onClick={() => {
                                                                        const metadataString = JSON.stringify(currentResult.metadata, null, 2);
                                                                        
                                                                        navigator.clipboard.writeText(metadataString).then(() => {
                                                                            showSuccessToast('Metadata copied to clipboard');
                                                                        }).catch(() => {
                                                                            showErrorToast('Failed to copy to clipboard');
                                                                        });
                                                                    }}
                                                                >
                                                                    Copy Metadata
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <Alert status="info">
                                                            <AlertIcon />
                                                            No metadata available for this execution
                                                        </Alert>
                                                    )}
                                                </Box>

                                                <Box>
                                                    <Text fontWeight="bold" mb={4}>System Information</Text>
                                                    <SimpleGrid columns={2} spacing={4}>
                                                        <Box>
                                                            <Text fontSize="sm" color={textColor} mb={1}>Updated At</Text>
                                                            <Text fontWeight="medium">
                                                                {formatDateTime(currentResult.updatedAtUtc || currentResult.executedAtUtc)}
                                                            </Text>
                                                        </Box>
                                                        <Box>
                                                            <Text fontSize="sm" color={textColor} mb={1}>Is Successful</Text>
                                                            <Badge colorScheme={currentResult.isSuccessful ? 'green' : 'red'}>
                                                                {currentResult.isSuccessful ? 'Yes' : 'No'}
                                                            </Badge>
                                                        </Box>
                                                        <Box>
                                                            <Text fontSize="sm" color={textColor} mb={1}>Is Pending</Text>
                                                            <Badge colorScheme={currentResult.isPending ? 'yellow' : 'gray'}>
                                                                {currentResult.isPending ? 'Yes' : 'No'}
                                                            </Badge>
                                                        </Box>
                                                        <Box>
                                                            <Text fontSize="sm" color={textColor} mb={1}>Status CSS Class</Text>
                                                            <Code fontSize="xs">{currentResult.statusCssClass || 'N/A'}</Code>
                                                        </Box>
                                                    </SimpleGrid>
                                                </Box>
                                            </VStack>
                                        </TabPanel>
                                    </TabPanels>
                                </Tabs>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                leftIcon={<MdFileDownload />}
                                onClick={() => handleExportSingleResult(currentResult)}
                                colorScheme="green"
                                variant="outline"
                                mr={3}
                            >
                                Export Details
                            </Button>
                            <Button onClick={onViewClose}>Close</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                    <ModalOverlay />
                    <ModalContent>
                        <ModalHeader>Confirm Delete</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            {currentResult && (
                                <VStack spacing={4} align="start">
                                    <Alert status="warning">
                                        <AlertIcon />
                                        <Box>
                                            <Text fontWeight="bold">This action cannot be undone</Text>
                                            <Text fontSize="sm">
                                                Are you sure you want to delete this result?
                                            </Text>
                                        </Box>
                                    </Alert>
                                    
                                    <Box>
                                        <Text fontSize="sm" color={textColor}>Client:</Text>
                                        <Text fontWeight="medium">{currentResult.clientName} (ID: {currentResult.clientId})</Text>
                                    </Box>
                                    
                                    <Box>
                                        <Text fontSize="sm" color={textColor}>Report:</Text>
                                        <Text fontWeight="medium">Code {currentResult.reportCode}</Text>
                                    </Box>
                                    
                                    <Box>
                                        <Text fontSize="sm" color={textColor}>Executed:</Text>
                                        <Text fontWeight="medium">{formatDateTime(currentResult.executedAtUtc)}</Text>
                                    </Box>
                                </VStack>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={handleDeleteResult}>
                                Delete Result
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            </VStack>
        </Box>
    );
}

export default ReportResult;